import { Injectable } from '@angular/core'
import { PlatformService } from '../platforms/platform.service'
import { BehaviorSubject } from 'rxjs'
import { LogService, LocalMedia, LocalMediaFile, Media } from 'htv-lib'
import { AssetStatus } from '../interfaces/dto/monitoring-dto'
import { LoaderService } from './loader.service'

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private medias: LocalMedia[] = []

  isLoading$ = new BehaviorSubject<boolean>(true)

  constructor(
    private loaderService: LoaderService,
    private logService: LogService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  async init(medias: Media[]): Promise<void> {
    this.isLoading$.next(true)
    this.logService.debug('[MediaService] Initializing with Humecloud data')

    const localMedias: LocalMedia[] = []

    // Retrieve local files
    let localMediaFiles: LocalMediaFile[] = []
    try {
      localMediaFiles = await this.platformService.getLocalMedias()
      this.logService.info(`[MediaService] Found ${localMediaFiles.length} local media files`)
    } catch (err: any) {
      this.logService.error(`[MediaService] An error occurred while retrieving local medias`)
    }

    // Compare from current local files
    for (const localMediaFile of localMediaFiles) {
      const index = medias.findIndex((media: Media) => media.id === localMediaFile.id)
      if (index > -1) {
        const media = medias[index]
        localMedias.push({
          id: media.id,
          media: media,
          downloaded: true,
          file: localMediaFile,
        })
      } else {
        this.logService.info(`[MediaService] Media ${localMediaFile.id} is no longer required, removing media...`)
        this.platformService.removeMedia(localMediaFile)
      }
    }

    // Compare from medias list from Humecloud
    const downloads = []
    const mediasToDownload = medias.filter(
      (media: Media) => localMediaFiles.findIndex((localMedia: LocalMediaFile) => media.id === localMedia.id) === -1
    )
    let completedDownloads = 0
    const setDownloadStep = () => {
      this.loaderService.setStep('PAGES.LOADER.DOWNLOADING_MEDIAS', {
        completed: completedDownloads,
        total: mediasToDownload.length,
      })
    }
    if (mediasToDownload.length > 0) {
      setDownloadStep()
    }
    const onDownloadSettled = () => {
      completedDownloads++
      setDownloadStep()
    }

    for (const media of medias) {
      const index = localMediaFiles.findIndex((localMedia: LocalMediaFile) => media.id === localMedia.id)
      if (index > -1) {
        // Media already downloaded, Could check size/md5/sha1
        this.logService.debug(`[MediaService] Media ${media.id} already downloaded`)
      } else {
        this.logService.debug(`[MediaService] Downloading media ${media.id}...`)
        const localMedia: LocalMedia = {
          id: media.id,
          media: media,
          downloaded: false,
          file: undefined,
        }
        localMedias.push(localMedia)
        downloads.push(
          this.platformService
            .downloadMedia(media)
            .then((localMediaFile: LocalMediaFile) => {
              this.logService.info(`[MediaService] Media ${media.id} has been downloaded`)
              localMedia.downloaded = true
              localMedia.file = localMediaFile
            })
            .catch((err: any) => {
              this.logService.error(`[MediaService] Media ${media.id} could not be downloaded, err = ${err?.message}`)
              localMedia.downloaded = false
              localMedia.file = undefined
            })
            .finally(() => onDownloadSettled())
        )
      }
    }

    return Promise.allSettled(downloads).then(() => {
      this.medias = localMedias
      if (mediasToDownload.length > 0) {
        this.loaderService.clearStep()
      }
      this.isLoading$.next(false)
      this.logService.info(`[MediaService] Initialization finished`)
    })
  }

  getMedia(id: string): LocalMedia | undefined {
    const index = this.medias.findIndex(media => media.id === id)
    if (index > -1) {
      return this.medias[index]
    } else {
      return undefined
    }
  }

  getMediaLocalUrl(media: LocalMedia | undefined): string | undefined {
    if (media?.downloaded && media?.file && media?.file?.url) {
      return media?.file?.url
    } else {
      return media?.media.url
    }
  }

  getMediaUrl(id: string): string | undefined {
    const media = this.getMedia(id)
    return this.getMediaLocalUrl(media)
  }

  getReport(): AssetStatus[] {
    return this.medias.map(media => ({
      assetId: media.id,
      fileName: media.file?.filename ?? '',
      downloaded: media.downloaded,
    }))
  }

  async clearMedias(): Promise<void> {
    let localMediaFiles: LocalMediaFile[] = []
    try {
      localMediaFiles = await this.platformService.getLocalMedias()
      this.logService.info(`[MediaService] Found ${localMediaFiles.length} local media files to be cleared`)
    } catch (err: any) {
      this.logService.error(`[MediaService] An error occurred while retrieving local medias`)
    }

    for (const localMediaFile of localMediaFiles) {
      await this.platformService.removeMedia(localMediaFile)
    }
  }
}
